import React, { useEffect, useState } from "react";
import { Box, Container, Divider, Typography } from "@mui/material";
import SectionWrapper from "../../components/wrappers/section";
import "./TermsOfUsage.css";

const tocItems = [
  { id: "introduction", label: "1. Introduction & Acceptance of Terms" },
  { id: "eligibility", label: "2. Eligibility & Parental Consent" },
  { id: "changes", label: "3. Changes to Terms" },
  { id: "privacy", label: "4. Privacy Policy" },
  { id: "account", label: "5. Account Registration & Security" },
  { id: "use-of-services", label: "6. Use of Services" },
  { id: "information", label: "7. Information We Collect" },
  { id: "how-we-use", label: "8. How We Use Your Information" },
  { id: "professionals", label: "9. Mental Health Professionals & Coaches" },
  { id: "ai-disclaimer", label: "10. AI Chatbot Use Disclaimer" },
  { id: "ownership", label: "11. Content Ownership & Licenses" },
  { id: "billing", label: "12. Subscription, Billing & Cancellation" },
  { id: "termination", label: "13. Termination" },
  { id: "intellectual-property", label: "14. Intellectual Property" },
  { id: "liability", label: "15. Disclaimers & Limitation of Liability" },
  { id: "indemnification", label: "16. Indemnification" },
  { id: "disputes", label: "17. Dispute Resolution & Arbitration" },
  { id: "crisis", label: "18. Not a Crisis Service" },
  { id: "export", label: "19. Export & Jurisdiction" },
  { id: "contact", label: "20. Contact" },
];

const TermsOfUsePage = () => {
  const [activeId, setActiveId] = useState("introduction");

  useEffect(() => {
    const sectionElements = tocItems
      .map((item) => document.getElementById(item.id))
      .filter(Boolean);

    const initialId =
      window.location.hash && document.querySelector(window.location.hash)
        ? window.location.hash.slice(1)
        : "introduction";

    setActiveId(initialId);

    if (!("IntersectionObserver" in window)) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
          }
        });
      },
      {
        rootMargin: "-25% 0px -65% 0px",
        threshold: 0,
      }
    );

    sectionElements.forEach((section) => observer.observe(section));

    return () => observer.disconnect();
  }, []);

  const handleTocClick = (event, id) => {
    event.preventDefault();

    const target = document.getElementById(id);
    if (!target) return;

    const offset = target.getBoundingClientRect().top + window.pageYOffset - 24;

    window.scrollTo({
      top: offset,
      behavior: "auto",
    });

    setActiveId(id);
    window.history.replaceState(null, "", `#${id}`);
  };

  return (
    <main className="tu-page">
      <Container maxWidth={false} className="tu-container">
        <Typography
          variant="h3"
          fontWeight={700}
          gutterBottom
          align="center"
          className="tu-title"
          sx={{ width: "100%", maxWidth: "840px", mx: "auto", textAlign: "center" }}
        >
          FYMAB Inc. or mindsherpa - Terms of Use
        </Typography>

        <div className="tu-meta">
          <span className="tu-pill">Effective: August 18, 2025</span>
          <span className="tu-pill">Last Updated: August 18, 2025</span>
        </div>

        <Divider className="tu-divider" />

        <div className="tu-layout">
          <aside className="tu-toc" aria-label="Terms of use contents">
            <p className="tu-toc-title">Contents</p>
            <ol className="tu-toc-list">
              {tocItems.map((item) => (
                <li key={item.id}>
                  <a
                    href={`#${item.id}`}
                    className={activeId === item.id ? "tu-active" : ""}
                    aria-current={activeId === item.id ? "true" : undefined}
                    onClick={(event) => handleTocClick(event, item.id)}
                  >
                    {item.label}
                  </a>
                </li>
              ))}
            </ol>
          </aside>

          <Box className="tu-card">
            <section className="tu-section" id="introduction">
              <SectionWrapper title="1. Introduction &amp; Acceptance of Terms">
        <Typography>
          These Terms of Use ("Terms") constitute a binding agreement between
          you ("User", "you") and FYMAB Inc. or mindsherpa. ("we", "us", or
          "our"), the owner and operator of the Mindsherpa wellness platform
          ("Services," or
          “Mindsherpa”). By accessing or using the Services, you acknowledge
          that you have read, understood, and agree to be bound by these Terms.
          If you do not agree, do not use our Services.
        </Typography>

        <Typography>
          Mindsherpa is a wellness and mindfulness platform. We are not a
          healthcare provider, and our Services are not a substitute for medical
          advice, treatment, or professional therapy.
        </Typography>
              </SectionWrapper>
            </section>

            <section className="tu-section" id="eligibility">
              <SectionWrapper title="2. Eligibility &amp; Parental Consent">
        <Typography>
          You must be at least 16 years of age to use our Services. By using the
          Services, you represent and warrant that you meet the legal age and
          have the authority to enter into these Terms. If you are a minor under
          the age of 18, you must have parental or guardian consent to use the
          Services. By creating an account, you confirm that you have such
          consent. We may require confirmation of consent at any time.
        </Typography>
              </SectionWrapper>
            </section>

            <section className="tu-section" id="changes">
              <SectionWrapper title="3. Changes to Terms">
        <Typography>
          FYMAB Inc. or mindsherpa. may modify these Terms at any time. If we
          make material changes, we will notify you via email or in-app
          notifications. Continued use of the Services after any changes
          constitutes acceptance of the revised Terms.
        </Typography>
              </SectionWrapper>
            </section>

            <section className="tu-section" id="privacy">
              <SectionWrapper title="4. Privacy Policy">
        <Typography>
          We take your privacy seriously. FYMAB Inc. or mindsherpa. collects and
          processes personal data in accordance with our Privacy Policy,
          including:
        </Typography>

        <ul>
          <li>Contact &amp; demographic information</li>
          <li>Usage data</li>
          <li>Wellness tracking entries</li>
          <li>AI chat interactions</li>
        </ul>

        <Typography>
          We do not knowingly collect personal data from children under 16. If
          we learn that we have, we will delete it promptly. If you believe a
          child under 16 has provided us with personal data, please contact us
          at the email address provided in Section 20.
        </Typography>
              </SectionWrapper>
            </section>

            <section className="tu-section" id="account">
              <SectionWrapper title="5. Account Registration &amp; Security">
        <Typography>
          You may be required to create an account to access some features. You
          agree to:
        </Typography>

        <ul>
          <li>Provide accurate and complete information</li>
          <li>Maintain confidentiality of your credentials</li>
          <li>Notify us immediately if you suspect unauthorized access</li>
        </ul>

        <Typography>
          You are solely responsible for all activities that occur under your
          account.
        </Typography>
              </SectionWrapper>
            </section>

            <section className="tu-section" id="use-of-services">
              <SectionWrapper title="6. Use of Services">
        <Typography>
          You agree to use the Services for lawful, personal, and non-commercial
          purposes only. You agree not to:
        </Typography>

        <ul>
          <li>
            Provide false, harmful, or infringing content, including but not
            limited to hate speech, harassment, or illegal material.
          </li>
          <li>Attempt to bypass security features.</li>
          <li>
            Use our Services for training any AI models, large language models,
            or other machine learning algorithms without our express written
            permission.
          </li>
          <li>
            Use the Services to provide or receive professional Healthcare
            Services.
          </li>
        </ul>
              </SectionWrapper>
            </section>

            <section className="tu-section" id="information">
              <SectionWrapper title="7. Information We Collect">
        <Typography>
          We collect various types of information to provide and improve our
          Services.
        </Typography>

        <Typography>
          <strong>Information You Provide:</strong> When you register for an
          account, you provide us with personal information such as your name,
          contact information (email address), and optional demographic
          information (age, gender, ethnicity, occupation). We collect optional
          ethnicity data to better understand and serve diverse communities;
          this data is used in an aggregated, anonymized form for analytical
          purposes. You are responsible for ensuring this information is
          accurate and up-to-date.
        </Typography>

        <Typography>
          <strong>Information from Third-Party Accounts:</strong> You may be
          able to access our Services using credentials from a Third-Party
          Account (e.g., Google, Facebook, Apple). By doing so, you permit us to
          access certain information from that account, which may include your
          name, email address, and profile picture. You can control what
          information is accessible by adjusting your privacy settings on the
          Third-Party Account.
        </Typography>
              </SectionWrapper>
            </section>

            <section className="tu-section" id="how-we-use">
              <SectionWrapper title="8. How We Use Your Information">
        <Typography>
          We use the information we collect for the following purposes:
        </Typography>

        <ul>
          <li>
            <strong>To Provide and Manage the Services:</strong> We use your
            information to operate and manage your personal wellness records,
            which includes the ability to store, manage, and share wellness
            information and access related resources and tools.
          </li>
          <li>
            <strong>For Communication:</strong> We may use your contact
            information to send you announcements, administrative messages, and
            service updates. We also may send you healthcare-related
            communications, and advertising, marketing, or promotional
            communications. You may opt out of receiving advertising, marketing,
            or promotional communications by following the instructions in those
            messages or by contacting us.
          </li>
          <li>
            <strong>For Analytics and Improvement:</strong> We may use your
            content to analyze and improve the Services. We also compile and use
            aggregated, de-identified data derived from your content to improve
            our Services. We may also extract and retain non-identifiable
            patterns from your content to improve our Services. This data is
            fully anonymized, meaning it cannot be linked back to you
            personally.
          </li>
          <li>
            <strong>To Facilitate AI Features:</strong> If the Services include
            an AI chatbot, we may use your chat interactions to facilitate the
            use of this feature. Any advice or information from the chatbot is
            generated electronically and is for informational purposes only. We
            do not use your personal chat interactions to train our AI models
            without your explicit consent.
          </li>
        </ul>
              </SectionWrapper>
            </section>

            <section className="tu-section" id="professionals">
              <SectionWrapper title="9. Mental Health Professionals &amp; Coaches">
        <Typography>
          If you register as a verified Mental Health Professional, you agree
          to:
        </Typography>

        <ul>
          <li>Provide proof of licensure</li>
          <li>
            Not deliver clinical therapy or Healthcare Services via our platform
          </li>
          <li>
            Acknowledge that your services are for wellness and educational
            purposes only
          </li>
        </ul>

        <Typography>
          FYMAB Inc. or mindsherpa. performs basic license validation checks for
          all listed professionals at the time of registration but does not
          verify the ongoing status or professional conduct of these
          individuals. We do not endorse or make any representations regarding
          the quality of their services.
        </Typography>
              </SectionWrapper>
            </section>

            <section className="tu-section" id="ai-disclaimer">
              <SectionWrapper title="10. AI Chatbot Use Disclaimer">
        <Typography>
          FYMAB Inc. Mindsherpa may include AI features such as chatbots. These
          are for wellness and informational purposes only. Advice provided by
          AI should not be considered medical, therapeutic, or legal advice. You
          acknowledge that AI content is generated algorithmically and may not
          be accurate or appropriate for your specific situation.
        </Typography>
              </SectionWrapper>
            </section>

            <section className="tu-section" id="ownership">
              <SectionWrapper title="11. Content Ownership &amp; Licenses">
        <Typography>
          You retain ownership of any content you submit. By submitting content,
          you grant FYMAB Inc. or mindsherpa. a non-exclusive, worldwide,
          royalty-free, perpetual, and sublicensable license to:
        </Typography>

        <ul>
          <li>Use it to deliver the Services to you.</li>
          <li>
            Derive insights or patterns from aggregated and anonymized data to
            improve our Services.
          </li>
          <li>
            Display or distribute content according to your sharing preferences.
          </li>
        </ul>

        <Typography>
          You must have the legal right to share any content you upload.
        </Typography>
              </SectionWrapper>
            </section>

            <section className="tu-section" id="billing">
              <SectionWrapper title="12. Subscription, Billing &amp; Cancellation">
        <Typography>
          FYMAB Inc. or Mindsherpa offers a 14-day free trial, after which you
          will be automatically charged a recurring subscription fee unless you
          cancel before the trial period ends. You can cancel at any time via
          your account settings. We use third-party payment processors (e.g.,
          Stripe) and do not store full payment data on our servers.
        </Typography>

        <Typography>
          Refunds are not provided for partial billing periods unless required
          by law.
        </Typography>
              </SectionWrapper>
            </section>

            <section className="tu-section" id="termination">
              <SectionWrapper title="13. Termination">
        <Typography>
          We reserve the right to suspend or terminate your account if:
        </Typography>

        <ul>
          <li>You breach these Terms</li>
          <li>You violate applicable laws</li>
          <li>You misuse or manipulate the AI or user systems</li>
        </ul>

        <Typography>
          You may also delete your account at any time. Upon deletion, we will
          remove your personal data as outlined in our Privacy Policy.
        </Typography>
              </SectionWrapper>
            </section>

            <section className="tu-section" id="intellectual-property">
              <SectionWrapper title="14. Intellectual Property">
        <Typography>
          All Mindsherpa content, branding, and software are the intellectual
          property of FYMAB Inc. or mindsherpa. or its licensors. You may not
          copy, modify, distribute, or reverse-engineer any part of our
          Services unless authorized.
        </Typography>
              </SectionWrapper>
            </section>

            <section className="tu-section" id="liability">
              <SectionWrapper title="15. Disclaimers &amp; Limitation of Liability">
        <Typography>
          The Services are provided "as is." FYMAB Inc. or mindsherpa.
          disclaims all warranties, including but not limited to the warranties
          of fitness for a particular purpose or non-infringement.
        </Typography>

        <Typography>
          To the fullest extent permitted by law, FYMAB Inc. or mindsherpa. is
          not liable for any indirect, incidental, or consequential damages, or
          any loss of data, profits, or goodwill. Our liability is limited to
          the amount you have paid in the past 12 months.
        </Typography>
              </SectionWrapper>
            </section>

            <section className="tu-section" id="indemnification">
              <SectionWrapper title="16. Indemnification">
        <Typography>
          You agree to indemnify and hold harmless FYMAB Inc. or mindsherpa.,
          its affiliates, and their employees and agents from any claim arising
          from your use of the Services or breach of these Terms.
        </Typography>
              </SectionWrapper>
            </section>

            <section className="tu-section" id="disputes">
              <SectionWrapper title="17. Dispute Resolution &amp; Arbitration">
        <Typography>
          This agreement and any disputes arising from it will be governed by
          the laws of the Province of Ontario, Canada. You agree to resolve
          disputes through binding individual arbitration and waive the right to
          participate in class actions.
        </Typography>

        <ul>
          <li>Small claims court is permitted where applicable.</li>
          <li>
            Arbitration will follow the AAA rules or a local equivalent in
            Canada.
          </li>
          <li>
            Notices of disputes should be sent to:{" "}
            <strong>info@mindsherpa.health</strong>
          </li>
        </ul>
              </SectionWrapper>
            </section>

            <section className="tu-section" id="crisis">
              <SectionWrapper title="18. Not a Crisis Service">
        <Typography>
          If you are in crisis or require immediate help, do not use Mindsherpa.
          Call 911 or your local emergency number.
        </Typography>
              </SectionWrapper>
            </section>

            <section className="tu-section" id="export">
              <SectionWrapper title="19. Export &amp; Jurisdiction">
        <Typography>
          FYMAB Inc. or mindsherpa. is based in Canada. By using our Services,
          you agree to comply with Canadian and U.S. export controls and local
          laws. You are responsible for compliance with local regulations when
          accessing our Services from outside Canada.
        </Typography>
              </SectionWrapper>
            </section>

            <section className="tu-section" id="contact">
              <SectionWrapper title="20. Contact">
        <Typography>
          Questions about these Terms should be directed to:
        </Typography>

        <Typography>
          <strong>info@mindsherpa.health</strong>
        </Typography>
              </SectionWrapper>
            </section>
          </Box>
        </div>
      </Container>
    </main>
  );
};

export default TermsOfUsePage;